import React, { useCallback, useReducer } from 'react';
import { styled } from '@this/constants/themes';
import A from '@this/components/shared/atoms/a';
import Button from '@this/components/shared/atoms/button';
import type Invoice from '@this/domain/invoice/invoice2';
import { Fetcher } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';

import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import { Link } from '@this/shared/ui/navigations/link';

interface Props {
  invoice: Invoice;
  freeeCompanyId: number | null;
}

interface FreeeFileboxResponse {
  receipt_id: number;
}

interface State {
  submitting: boolean;
  error: string | null;
}

type Action =
  | { type: 'SUBMITTING_START' }
  | { type: 'SUBMITTING_SUCCESS' }
  | { type: 'SUBMITTING_ERROR'; payload: string };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SUBMITTING_START':
      return { ...state, submitting: true, error: null };
    case 'SUBMITTING_SUCCESS':
      return { ...state, submitting: false };
    case 'SUBMITTING_ERROR':
      return { ...state, submitting: false, error: action.payload };
    default:
      return state;
  }
};

const intialState: State = {
  submitting: false,
  error: null
};

const FreeeFilebox = ({ invoice, freeeCompanyId }: Props) => {
  const [state, dispatch] = useReducer(reducer, intialState);
  const { submitting, error } = state;

  const postFreeeFilebox = useCallback(async () => {
    dispatch({ type: 'SUBMITTING_START' });
    const params = {
      invoice_id: invoice.id
    };
    try {
      const response = await Fetcher.post<FreeeFileboxResponse>('/organization/freee/receipt', params);
      invoice.freeeReceiptId = response.receipt_id;
      dispatch({ type: 'SUBMITTING_SUCCESS' });
    } catch (e) {
      if ((e as any).response?.status === 400 || (e as any).response?.status === 401) {
        dispatch({ type: 'SUBMITTING_ERROR', payload: (e as any).response.data.message });
      } else {
        dispatch({
          type: 'SUBMITTING_ERROR',
          payload: '通信エラーが発生しました。時間をおいて再度お試しください。'
        });
        reportError(e as Error);
      }
    }
  }, [invoice, dispatch]);

  return (
    <li>
      <h3>ファイルボックス</h3>
      <p>請求書PDFをfreeeのファイルボックスに送信できます。</p>
      {submitting ? (
        <SimpleLoading />
      ) : freeeCompanyId === null ? (
        <>
          <Description>
            ※取引登録をご利用頂くにはシステム設定の外部連携、freeeの設定から事業所を設定して頂く必要がございます。
          </Description>
          <ButtonArea>
            <Link color="primary" href="/organization/setting">
              freee連携の設定
            </Link>
          </ButtonArea>
        </>
      ) : (
        <ButtonArea>
          {invoice.freeeReceiptId ? (
            <>
              送信済み（
              <A
                href={`https://secure.freee.co.jp/receipts/${invoice.freeeReceiptId}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                freeeでファイルを表示
              </A>
              ）
            </>
          ) : (
            <>
              <TheButton onClick={() => postFreeeFilebox()}>freeeファイルボックスに送信</TheButton>
              {error && (
                <div className="red" style={{ marginTop: '5px' }}>
                  {error}
                </div>
              )}
            </>
          )}
        </ButtonArea>
      )}
    </li>
  );
};

const TheButton = styled(Button)`
  width: fit-content;
  margin-right: 10px;
`;

const ButtonArea = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const Description = styled.div`
  font-size: 11px;
  color: ${props => props.theme.grayColor};
  padding-top: 10px;
`;

export default FreeeFilebox;
